import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  Result,
  InputDiv,
  RadioInputDiv,
  MathDiv,
  ActionButtonsRow,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  CopyHTMLButton,
  AlertDiv,
  RadioWrapper,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { toast } from 'react-toastify'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { MdCheckCircle, MdError } from 'react-icons/md'
import { Tooltip } from 'react-tooltip'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
export function MansoryShear() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const mansoryGradient = 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)'
  const [fpk, setFpk] = useState('')
  const [fyk, setFyk] = useState('')
  const [fgk, setFgk] = useState('')
  const [fa, setFa] = useState('')
  const [b, setB] = useState('')
  const [d, setD] = useState('')
  const [as, setAs] = useState(0)
  const [vk, setVk] = useState(0)
  const [gammaf, setGammaf] = useState(1.4)
  const [sigma, setSigma] = useState('')
  const [situation, setSituation] = useState(1)
  const [isFormValid, setIsFormValid] = useState(false)
  const [data, setData] = useState({})
  const [result, setResult] = useState('')
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [touched, setTouched] = useState({})
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'mansory-shear-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFpk(entry.fpk)
      setFyk(entry.fyk)
      setFgk(entry.fgk)
      setFa(entry.fa)
      setB(entry.b)
      setD(entry.d)
      setAs(entry.as)
      setVk(entry.vk)
      setGammaf(entry.gammaf || 1.4)
      setSigma(entry.sigma)
      setSituation(entry.situation)
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback(
    (name, value) => {
      if (situation == 1 && ['fa', 'b', 'd', 'vk', 'sigma'].includes(name)) {
        if (value === '' || value === null || value === undefined) {
          return 'Campo obrigatório'
        }

        const numValue = Number(value)

        if (isNaN(numValue)) {
          return 'Valor deve ser um número'
        }

        if (name !== 'rhoMin' && numValue <= 0) {
          return 'Valor deve ser maior que 0'
        }

        return null
      }
      if ((situation == 2 || situation == 3) && ['fgk', 'fpk', 'fyk', 'b', 'd', 'as', 'vk'].includes(name)) {
        if (value === '' || value === null || value === undefined) {
          return 'Campo obrigatório'
        }

        const numValue = Number(value)

        if (isNaN(numValue)) {
          return 'Valor deve ser um número'
        }

        if (name !== 'rhoMin' && numValue <= 0) {
          return 'Valor deve ser maior que 0'
        }

        return null
      }

      return null
    },
    [situation]
  )

  useEffect(() => {
    const newErrors = {}

    newErrors.fpk = validateField('fpk', fpk)
    newErrors.fyk = validateField('fyk', fyk)
    newErrors.fgk = validateField('fgk', fgk)
    newErrors.fa = validateField('fa', fa)
    newErrors.b = validateField('b', b)
    newErrors.d = validateField('d', d)
    newErrors.as = validateField('as', as)
    newErrors.vk = validateField('vk', vk)
    newErrors.gammaf = validateField('gammaf', gammaf)
    newErrors.sigma = validateField('sigma', sigma)
    newErrors.situation = validateField('situation', situation)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [fpk, fyk, fgk, fa, b, d, as, vk, gammaf, sigma, situation, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const data = {
        fpk,
        fyk,
        fgk,
        fa,
        b,
        d,
        as,
        vd: vk * gammaf,
        sigma,
        situation,
      }

      const response = await api.post('/mansory/shear', data)

      setData(data)

      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fpk,
        fyk,
        fgk,
        fa,
        b,
        d,
        as,
        vk,
        gammaf,
        sigma,
        situation,
        result: response.data,
        data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }

    event.preventDefault()
  }

  function handleSave() {
    const stateData = {
      fpk,
      fyk,
      fgk,
      fa,
      b,
      d,
      vk,
      gammaf,
      sigma,
      situation,
      isFormValid,
      data,
      result,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Alvenaria Cisalhamento.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setFpk(jsonData.fpk)
        setFyk(jsonData.fyk)
        setFgk(jsonData.fgk)
        setFa(jsonData.fa)
        setB(jsonData.b)
        setD(jsonData.d)
        setAs(jsonData.as)
        setVk(jsonData.vk)
        setGammaf(jsonData.gammaf || 1.4)
        setSigma(jsonData.sigma)
        setSituation(jsonData.situation)
        setIsFormValid(jsonData.isFormValid)
        setData(jsonData.data)
        setResult(jsonData.result)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o arquivo. Verifique o formato.')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
      errorMessage: 'Erro ao copiar os cálculos. Tente novamente.',
    })
  }
  return (
    <PageContainer>
      <PageHeader $gradient={mansoryGradient}>
        <div className="header-content">
          <BackButton to="/alvenaria">
            <FiArrowLeft /> Voltar para Alvenaria
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/alvenaria">Alvenaria</a>
            <span>/</span>
            <span>Cisalhamento</span>
          </div>
          <h1>🧱 Cisalhamento em Alvenaria Estrutural</h1>
          <p className="subtitle">
            Dimensionamento e verificação de elementos de alvenaria estrutural submetidos ao cisalhamento
          </p>
          <div className="badges">
            <Badge>📋 NBR 16868-1:2020</Badge>
            <Badge>🧮 Cálculo de Armadura</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard>
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta realiza o dimensionamento de elementos de alvenaria estrutural submetidos ao cisalhamento,
              conforme a NBR 16868-1:2020.
            </p>
            <p>
              O cálculo verifica a resistência ao cisalhamento para paredes, vergas e vigas, determinando a área de aço
              necessária quando aplicável, considerando as propriedades dos materiais e a geometria da seção.
            </p>
            <div className="norms">
              <span>NBR 16868-1:2020</span>
              <span>Resistência ao Cisalhamento</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <form onSubmit={handleCalculate}>
            <RadioInputDiv>
              <span>Tipo</span>
              <div>
                <RadioWrapper>
                  <input
                    type="radio"
                    name="situation"
                    id="situation1"
                    value={1}
                    checked={situation === 1}
                    onChange={(e) => {
                      setSituation(Number(e.target.value))
                    }}
                  />
                  <span>Parede</span>
                </RadioWrapper>
                <RadioWrapper>
                  <input
                    type="radio"
                    name="situation"
                    id="situation2"
                    value={2}
                    checked={situation === 2}
                    onChange={(e) => {
                      setSituation(Number(e.target.value))
                    }}
                  />
                  <span>Verga</span>
                </RadioWrapper>
                <RadioWrapper>
                  <input
                    type="radio"
                    name="situation"
                    id="situation3"
                    value={3}
                    checked={situation === 3}
                    onChange={(e) => {
                      setSituation(Number(e.target.value))
                    }}
                  />
                  <span>Viga</span>
                </RadioWrapper>
              </div>
            </RadioInputDiv>
            <h3>Materiais</h3>
            {situation != 1 && (
              <InputDiv>
                <span>
                  Resistência Característica do Graute (f<sub>gk</sub>, MPa)
                </span>
                <ValidatedInput
                  name="fgk"
                  type="number"
                  value={fgk || ''}
                  onChange={(event) => setFgk(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}
            {situation != 1 && (
              <InputDiv>
                <span>
                  Resistência Característica do Prisma (f<sub>pk</sub>, MPa)
                </span>
                <ValidatedInput
                  name="fpk"
                  type="number"
                  value={fpk || ''}
                  onChange={(event) => setFpk(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}
            {situation != 1 && (
              <InputDiv>
                <span>
                  Resistência Característica do Aço (f<sub>yk</sub>, MPa)
                </span>
                <ValidatedInput
                  name="fyk"
                  type="number"
                  value={fyk || ''}
                  onChange={(event) => setFyk(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}
            {situation === 1 && (
              <InputDiv>
                <span>
                  Resistência Média de Compressão da Argamassa (f<sub>a</sub>, MPa)
                </span>
                <ValidatedInput
                  name="fa"
                  type="number"
                  value={fa || ''}
                  onChange={(event) => setFa(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}
            <h3>Geometria</h3>
            {situation === 1 ? (
              <InputDiv>
                <span>Espessura (cm)</span>
                <ValidatedInput
                  name="b"
                  type="number"
                  value={b || ''}
                  onChange={(event) => setB(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            ) : (
              <InputDiv>
                <span>Largura (cm)</span>
                <ValidatedInput
                  name="b"
                  type="number"
                  value={b || ''}
                  onChange={(event) => setB(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}
            {situation === 1 ? (
              <InputDiv>
                <span>Comprimento (cm)</span>
                <ValidatedInput
                  name="d"
                  type="number"
                  value={d || ''}
                  onChange={(event) => setD(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            ) : (
              <InputDiv>
                <span>Altura útil (cm)</span>
                <ValidatedInput
                  name="d"
                  type="number"
                  value={d || ''}
                  onChange={(event) => setD(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}
            {situation != 1 && (
              <InputDiv>
                <span>
                  A<sub>s,longitudinal</sub> (cm²)
                </span>
                <ValidatedInput
                  name="as"
                  type="number"
                  value={as}
                  onChange={(event) => setAs(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}
            <h3>Esforços</h3>
            <InputDiv>
              <span>
                V<sub>k</sub> (kN)
              </span>
              <ValidatedInput
                name="vk"
                type="number"
                value={vk || ''}
                onChange={(event) => setVk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Coeficiente de ponderação γf</span>
              <ValidatedInput
                name="gammaf"
                type="number"
                value={gammaf || ''}
                onChange={(event) => setGammaf(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            {situation === 1 && (
              <InputDiv>
                <span>Tensão normal (MPa)</span>
                <ValidatedInput
                  name="sigma"
                  type="number"
                  value={sigma}
                  onChange={(event) => setSigma(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}

            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            {data.situation === 1 && (
              <>
                <Result $isRecalculating={isRecalculating}>
                  {isRecalculating && <LoadingOverlay message="Recalculando..." />}
                  <h2>Resultados</h2>
                  <MathDiv>
                    <BlockMath math={`\\dfrac{V_k \\cdot \\gamma_f}{b \\cdot h} \\leq \\dfrac{f_{vk}}{\\gamma_m}`} />
                    {result.check.noSteel ? (
                      <AlertDiv success={true}>
                        <BlockMath
                          math={`${result.check.factor1.toFixed(4)} \\text{ kN/cm²} \\leq ${result.check.factor2.toFixed(4)} \\text{ kN/cm²}`}
                        />
                        <MdCheckCircle />
                      </AlertDiv>
                    ) : (
                      <>
                        <AlertDiv>
                          <BlockMath
                            math={`${result.check.factor1.toFixed(4)} \\text{ kN/cm²} > ${result.check.factor2.toFixed(4)} \\text{ kN/cm²}`}
                          />
                          <MdError />
                        </AlertDiv>
                        <p>Cortante acima do limite permitido.</p>
                      </>
                    )}
                  </MathDiv>
                </Result>
                <Result className="results-section" $isRecalculating={isRecalculating}>
                  {isRecalculating && <LoadingOverlay />}
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
                  >
                    <h2>Cálculos</h2>
                    <CopyHTMLButton type="button" onClick={handleCopyResults}>
                      📋 Copiar Cálculos
                    </CopyHTMLButton>
                  </div>
                  <MathDiv>
                    {result.fvk.group == 1 && (
                      <>
                        <BlockMath math={`f_{vk} = 0,1 + 0,5 \\cdot \\sigma \\leq 1`} />
                        <BlockMath math={`f_{vk} = 0,1 + 0,5 \\cdot ${data.sigma.toFixed(2)} \\leq 1`} />
                        <BlockMath math={`\\boxed{f_{vk} = ${result.fvk.fvk.toFixed(2)} \\leq 1 \\text{ MPa}}`} />
                      </>
                    )}
                    {result.fvk.group == 2 && (
                      <>
                        <BlockMath math={`f_{vk} = 0,15 + 0,5 \\cdot \\sigma \\leq 1,4`} />
                        <BlockMath math={`f_{vk} = 0,15 + 0,5 \\cdot ${data.sigma.toFixed(2)} \\leq 1,4`} />
                        <BlockMath math={`\\boxed{f_{vk} = ${result.fvk.fvk.toFixed(2)} \\leq 1,4 \\text{ MPa}}`} />
                      </>
                    )}
                    {result.fvk.group == 3 && (
                      <>
                        <BlockMath math={`f_{vk} = 0,35 + 0,5 \\cdot \\sigma \\leq 1,7`} />
                        <BlockMath math={`f_{vk} = 0,35 + 0,5 \\cdot ${data.sigma.toFixed(2)} \\leq 1,7`} />
                        <BlockMath math={`\\boxed{f_{vk} = ${result.fvk.fvk.toFixed(2)} \\leq 1,7 \\text{ MPa}}`} />
                      </>
                    )}
                    <BlockMath math={`\\dfrac{V_k \\cdot \\gamma_f}{b \\cdot h} \\leq \\dfrac{f_{vk}}{\\gamma_m}`} />
                    <BlockMath
                      math={`\\dfrac{${data.vd}}{${data.b} \\cdot ${data.d}} \\leq \\dfrac{${result.fvk.fvk.toFixed(2)}}{2}`}
                    />
                    {result.check.noSteel ? (
                      <AlertDiv success={true}>
                        <BlockMath
                          math={`\\boxed{${result.check.factor1.toFixed(4)} \\text{ kN/cm²} \\leq ${result.check.factor2.toFixed(4)} \\text{ kN/cm²}}`}
                        />
                        <MdCheckCircle />
                      </AlertDiv>
                    ) : (
                      <>
                        <AlertDiv>
                          <BlockMath
                            math={`\\boxed{${result.check.factor1.toFixed(4)} \\text{ kN/cm²} > ${result.check.factor2.toFixed(4)} \\text{ kN/cm²}}`}
                          />
                          <MdError />
                        </AlertDiv>
                        <p>Cortante acima do limite permitido.</p>
                      </>
                    )}
                  </MathDiv>
                </Result>
              </>
            )}
            {data.situation == 2 && (
              <>
                <Result $isRecalculating={isRecalculating}>
                  {isRecalculating && <LoadingOverlay message="Recalculando..." />}
                  <h2>Resultados</h2>
                  {result.check.noSteel ? (
                    <>
                      <MathDiv>
                        <BlockMath
                          math={`\\dfrac{V_k \\cdot \\gamma_f}{b \\cdot h} \\leq \\dfrac{f_{vk}}{\\gamma_m}`}
                        />
                        <AlertDiv success={true}>
                          <BlockMath
                            math={`${result.check.factor1.toFixed(4)} \\text{ kN/cm²} \\leq ${result.check.factor2.toFixed(4)} \\text{ kN/cm²}`}
                          />
                          <MdCheckCircle />
                        </AlertDiv>
                        <span>Sem necessidade de armadura.</span>
                      </MathDiv>
                    </>
                  ) : (
                    <>
                      <h3>Teste de verga sem armadura</h3>
                      <MathDiv>
                        <BlockMath
                          math={`\\dfrac{V_k \\cdot \\gamma_f}{b \\cdot h} \\leq \\dfrac{f_{vk}}{\\gamma_m}`}
                        />
                        <AlertDiv>
                          <BlockMath
                            math={`${result.check.factor1.toFixed(4)} \\text{ kN/cm²} > ${result.check.factor2.toFixed(4)} \\text{ kN/cm²}`}
                          />
                          <MdError />
                        </AlertDiv>
                        <p>Será necessário adicionar armadura.</p>
                      </MathDiv>
                      {result.vsCheck ? (
                        <>
                          <h3>Cálculo da armadura</h3>
                          <MathDiv>
                            <BlockMath math={`V_s \\leq 0,4 \\cdot b \\cdot d \\cdot \\dfrac{f_v}{\\gamma_m}`} />
                            <AlertDiv success={true}>
                              <BlockMath math={`${result.vs.toFixed(2)} \\leq ${result.vsCheckValue.toFixed(2)}`} />
                              <MdCheckCircle />
                            </AlertDiv>
                            <BlockMath math={`A_{sw} = ${result.asw.toFixed(2)} \\text{ cm²/m}`} />
                          </MathDiv>
                        </>
                      ) : (
                        <MathDiv>
                          <AlertDiv>
                            <BlockMath math={`V_s = ${result.vs.toFixed(2)} > ${result.vsCheckValue.toFixed(2)}`} />
                            <MdError />
                          </AlertDiv>
                          <p>Cortante acima do limite de norma.</p>
                        </MathDiv>
                      )}
                    </>
                  )}
                </Result>
                <Result className="results-section" $isRecalculating={isRecalculating}>
                  {isRecalculating && <LoadingOverlay />}
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
                  >
                    <h2>Cálculos</h2>
                    <CopyHTMLButton type="button" onClick={handleCopyResults}>
                      📋 Copiar Cálculos
                    </CopyHTMLButton>
                  </div>
                  <h3>Resistência de cisalhamento</h3>
                  <MathDiv>
                    <BlockMath
                      math={`\\rho = \\dfrac{A_s}{b \\cdot d} = \\dfrac{${data.as}}{${data.b} \\cdot ${data.d}} = ${(result.rho * 100).toFixed(2)} \\%`}
                    />
                    <BlockMath math={`f_{vk} = 0,35 + 17,5 \\cdot \\rho`} />
                    <BlockMath math={`f_{vk} = 0,35 + 17,5 \\cdot ${(result.rho * 100).toFixed(2)} \\%`} />
                    <BlockMath
                      math={`f_{vk} = ${(result.fvk * 10).toFixed(3)} \\text{ MPa} =  ${result.fvk.toFixed(4)} \\text{ kN/cm²}`}
                    />
                  </MathDiv>
                  <h3>Testando verga sem armadura</h3>
                  <MathDiv>
                    <BlockMath math={`\\dfrac{V_k \\cdot \\gamma_f}{b \\cdot h} \\leq \\dfrac{f_{vk}}{\\gamma_m}`} />
                    <BlockMath
                      math={`\\dfrac{${data.vd.toFixed(2)}}{${data.b} \\cdot ${data.d}} \\leq \\dfrac{${result.fvk.toFixed(4)}}{2}`}
                    />
                  </MathDiv>
                  {result.check.noSteel ? (
                    <MathDiv>
                      <AlertDiv success={true}>
                        <BlockMath
                          math={`\\boxed{${result.check.factor1.toFixed(4)} \\text{ kN/cm²} \\leq ${result.check.factor2.toFixed(4)} \\text{ kN/cm²}}`}
                        />
                        <MdCheckCircle />
                      </AlertDiv>
                      <span>Sem necessidade de armadura.</span>
                    </MathDiv>
                  ) : (
                    <>
                      <MathDiv>
                        <AlertDiv>
                          <BlockMath
                            math={`${result.check.factor1.toFixed(4)} \\text{ kN/cm²} > ${result.check.factor2.toFixed(4)} \\text{ kN/cm²}`}
                          />
                          <MdError />
                        </AlertDiv>
                        <p>Será necessário adicionar armadura.</p>
                      </MathDiv>
                      <h3>Cálculo da armadura</h3>
                      <MathDiv>
                        <BlockMath
                          math={`V_a = b \\cdot d \\cdot \\dfrac{f_{vk}}{\\gamma_m} = ${data.b} \\cdot ${data.d} \\cdot \\dfrac{${(result.fvk * 10).toFixed(3)}}{2}`}
                        />
                        <BlockMath math={`V_a = ${result.va.toFixed(2)} \\text{ kN}`} />
                        <BlockMath math={`V_s = V_d - V_a = ${data.vd} - ${result.va.toFixed(2)}`} />
                        <BlockMath math={`\\boxed{V_s = ${result.vs.toFixed(2)} \\text{ kN}}`} />
                        <BlockMath math={`V_s \\leq 0,4 \\cdot b \\cdot d \\cdot \\dfrac{f_v}{\\gamma_m}`} />
                        <BlockMath
                          math={`f_v = f_{pk}^{\\frac{1}{2}} = ${data.fpk}^{\\frac{1}{2}} = ${(result.fv * 10).toFixed(2)} \\text{ MPa}`}
                        />
                        <BlockMath
                          math={`V_s \\leq 0,4 \\cdot ${data.b} \\cdot ${data.d} \\cdot \\dfrac{${result.fv.toFixed(3)}}{2}`}
                        />
                        {result.vsCheck ? (
                          <AlertDiv success={true}>
                            <BlockMath math={`${result.vs.toFixed(2)} \\leq ${result.vsCheckValue.toFixed(2)}`} />
                            <MdCheckCircle />
                          </AlertDiv>
                        ) : (
                          <>
                            <AlertDiv>
                              <BlockMath math={`${result.vs.toFixed(2)} > ${result.vsCheckValue.toFixed(2)}`} />
                              <MdError />
                            </AlertDiv>
                            <p>Cortante acima do limite de norma.</p>
                          </>
                        )}
                      </MathDiv>
                      {result.vsCheck && (
                        <>
                          <h3>Área de aço</h3>
                          <MathDiv>
                            <BlockMath math={`A_{sw,calc} = \\dfrac{V_s \\cdot s}{0,75 \\cdot f_{yd} \\cdot d}`} />
                            <BlockMath
                              math={`A_{sw,calc} = \\dfrac{${result.vs.toFixed(2)} \\cdot 100}{0,75 \\cdot ${result.fyd.toFixed(2)} \\cdot ${data.d}}`}
                            />
                            <BlockMath math={`A_{sw,calc} = ${result.aswcalc.toFixed(2)} \\text{ cm²/m}`} />
                            <BlockMath math={`A_{sw,min} = \\rho_{min} \\cdot b \\cdot 100`} />
                            <BlockMath math={`\\rho_{min} = ${result.rhoMinValue}`} />
                            <BlockMath math={`A_{sw,min} = ${result.rhoMinValue} \\cdot ${data.b} \\cdot 100`} />
                            <BlockMath math={`A_{sw,min} = ${result.aswmin.toFixed(2)} \\text{ cm²/m}`} />
                            <BlockMath math={`\\boxed{A_{sw} = ${result.asw.toFixed(2)} \\text{ cm²/m}}`} />
                          </MathDiv>
                        </>
                      )}
                    </>
                  )}
                </Result>
              </>
            )}
            {data.situation == 3 && (
              <>
                <Result $isRecalculating={isRecalculating}>
                  {isRecalculating && <LoadingOverlay message="Recalculando..." />}
                  <h2>Resultados</h2>
                  <MathDiv>
                    <BlockMath math={`V_s \\leq 0,4 \\cdot b \\cdot d \\cdot \\dfrac{f_v}{\\gamma_m}`} />
                    {result.vsCheck ? (
                      <>
                        <AlertDiv success={true}>
                          <BlockMath math={`${result.vs.toFixed(2)} \\leq ${result.vsCheckValue.toFixed(2)}`} />
                          <MdCheckCircle />
                        </AlertDiv>
                        <BlockMath math={`A_{sw} = ${result.asw.toFixed(2)} \\text{ cm²/m}`} />
                      </>
                    ) : (
                      <>
                        <AlertDiv>
                          <BlockMath math={`V_s = ${result.vs.toFixed(2)} > ${result.vsCheckValue.toFixed(2)}`} />
                          <MdError />
                        </AlertDiv>
                        <p>Cortante acima do limite de norma.</p>
                      </>
                    )}
                  </MathDiv>
                </Result>
                <Result className="results-section" $isRecalculating={isRecalculating}>
                  {isRecalculating && <LoadingOverlay />}
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
                  >
                    <h2>Cálculos</h2>
                    <CopyHTMLButton type="button" onClick={handleCopyResults}>
                      📋 Copiar Cálculos
                    </CopyHTMLButton>
                  </div>
                  <h3>Resistência de cisalhamento</h3>
                  <MathDiv>
                    <BlockMath
                      math={`\\rho = \\dfrac{A_s}{b \\cdot d} = \\dfrac{${data.as}}{${data.b} \\cdot ${data.d}} = ${(result.rho * 100).toFixed(2)} \\%`}
                    />
                    <BlockMath math={`f_{vk} = 0,35 + 17,5 \\cdot \\rho`} />
                    <BlockMath math={`f_{vk} = 0,35 + 17,5 \\cdot ${(result.rho * 100).toFixed(2)} \\%`} />
                    <BlockMath
                      math={`f_{vk} = ${(result.fvk * 10).toFixed(3)} \\text{ MPa} =  ${result.fvk.toFixed(4)} \\text{ kN/cm²}`}
                    />
                    <BlockMath
                      math={`V_a = b \\cdot d \\cdot \\dfrac{f_{vk}}{\\gamma_m} = ${data.b} \\cdot ${data.d} \\cdot \\dfrac{${(result.fvk * 10).toFixed(3)}}{2}`}
                    />
                    <BlockMath math={`V_a = ${result.va.toFixed(2)} \\text{ kN}`} />
                    <BlockMath math={`V_s = V_d - V_a = ${data.vd} - ${result.va.toFixed(2)}`} />
                    <BlockMath math={`\\boxed{V_s = ${result.vs.toFixed(2)} \\text{ kN}}`} />
                    <BlockMath math={`V_s \\leq 0,4 \\cdot b \\cdot d \\cdot \\dfrac{f_v}{\\gamma_m}`} />
                    <BlockMath
                      math={`f_v = f_{pk}^{\\frac{1}{2}} = ${data.fpk}^{\\frac{1}{2}} = ${(result.fv * 10).toFixed(2)} \\text{ MPa}`}
                    />
                    <BlockMath
                      math={`V_s \\leq 0,4 \\cdot ${data.b} \\cdot ${data.d} \\cdot \\dfrac{${result.fv.toFixed(3)}}{2}`}
                    />
                  </MathDiv>
                  {result.vsCheck ? (
                    <>
                      <MathDiv>
                        <AlertDiv success={true}>
                          <BlockMath math={`${result.vs.toFixed(2)} \\leq ${result.vsCheckValue.toFixed(2)}`} />
                          <MdCheckCircle />
                        </AlertDiv>
                      </MathDiv>
                      <h3>Área de aço</h3>
                      <MathDiv>
                        <BlockMath math={`A_{sw,calc} = \\dfrac{V_s \\cdot s}{0,75 \\cdot f_{yd} \\cdot d}`} />
                        <BlockMath
                          math={`A_{sw,calc} = \\dfrac{${result.vs.toFixed(2)} \\cdot 100}{0,75 \\cdot ${result.fyd.toFixed(2)} \\cdot ${data.d}}`}
                        />
                        <BlockMath math={`A_{sw,calc} = ${result.aswcalc.toFixed(2)} \\text{ cm²/m}`} />
                        <BlockMath math={`A_{sw,min} = \\rho_{min} \\cdot b \\cdot 100`} />
                        <BlockMath math={`\\rho_{min} = ${result.rhoMinValue}`} />
                        <BlockMath math={`A_{sw,min} = ${result.rhoMinValue} \\cdot ${data.b} \\cdot 100`} />
                        <BlockMath math={`A_{sw,min} = ${result.aswmin.toFixed(2)} \\text{ cm²/m}`} />
                        <BlockMath math={`\\boxed{A_{sw} = ${result.asw.toFixed(2)} \\text{ cm²/m}}`} />
                      </MathDiv>
                    </>
                  ) : (
                    <>
                      <MathDiv>
                        <AlertDiv>
                          <BlockMath math={`V_s = ${result.vs.toFixed(2)} > ${result.vsCheckValue.toFixed(2)}`} />
                          <MdError />
                        </AlertDiv>
                        <p>Cortante acima do limite de norma.</p>
                      </MathDiv>
                    </>
                  )}
                </Result>
              </>
            )}
          </>
        )}
      </Container>
      <Tooltip id="mansory-shear-tooltip" style={{ whiteSpace: 'pre-line' }} place="top" />
    </PageContainer>
  )
}
